"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/components/providers/auth-provider";
import { useLibrary } from "@/features/dashboard/hooks";
import { fetcher } from "@/lib/api";
import { queryKeys } from "@/lib/query-keys";
import { coalesceCourseMoney, formatCourseMoneyEnIn, type CoursePrice } from "../api";
import { getOwnershipCopy } from "../lib/ownership-copy";
import { locales, type Locale } from "@/lib/i18n";
import { signInHref } from "@/lib/post-login-redirect";

type CatalogSlugResponse = {
  item: { _id: string };
  isOwned: boolean;
};

type Props = {
  courseId: string;
  slug: string;
  price: CoursePrice;
  locale: string;
  className?: string;
  size?: "default" | "sm";
  firstLessonId?: string | null;
  /** When provided, include in the order POST body and checkout URL */
  pricingPlanId?: string;
  /** Overrides the action button label for non-owned, non-loading states */
  enrollLabel?: string;
  /**
   * When provided AND the user already owns this course (on a different plan),
   * the "Continue Learning" CTA is replaced with an "Upgrade to <target>" link
   * that routes to /dashboard/upgrade/<courseId>?target=<upgradeToPlanId>.
   */
  upgradeToPlanId?: string;
  /** Label override used only on the upgrade CTA. */
  upgradeLabel?: string;
  /**
   * When the user owns the course but the selected tier is not their current
   * plan and is not a valid upgrade target (e.g. not in `upgradeableTo[]`),
   * show a disabled button instead of "Continue Learning".
   */
  upgradeUnavailableReason?: string;
  /** Button label when `upgradeUnavailableReason` is set (defaults to "Upgrade"). */
  upgradeUnavailableLabel?: string;
  /** Purchase-gate lock (e.g. course eligibility not yet passed). */
  lockedReason?: string;
  lockedLabel?: string;
  /**
   * Owned course, non-actionable tier (peer plan or legacy peer card),
   * replaces Continue / upgrade with a neutral notice (no button).
   */
  enrolledPeerNotice?: string;
};

function formatPrice(price: CoursePrice): string {
  const list = coalesceCourseMoney(price.list);
  const sale = price.sale;
  const amount =
    sale !== undefined && Number.isFinite(sale) && sale < list ? sale : list;
  const symbol = price.currency === "BDT" ? "৳" : "$";
  return `${symbol}${formatCourseMoneyEnIn(amount)}`;
}

export function CourseEnrollCta({
  courseId,
  slug,
  price,
  locale,
  className,
  size = "default",
  firstLessonId,
  pricingPlanId,
  enrollLabel,
  upgradeToPlanId,
  upgradeLabel,
  upgradeUnavailableReason,
  upgradeUnavailableLabel,
  lockedReason,
  lockedLabel,
  enrolledPeerNotice,
}: Props) {
  const { isAuthenticated, isLoading: authLoading, getToken } = useAuth();
  const token = getToken();
  const { data: library, isLoading: libLoading } = useLibrary(token ?? undefined);
  const router = useRouter();
  const t = useTranslations("courseDetail");
  // Carry a coupon from share/marketing links through to checkout (e.g. ?coupon=CSE6566).
  // Read after mount, not with `useSearchParams`: on a prerendered course page that
  // hook makes the button render nothing until the page's script runs (`SITE-CRS01`).
  const [couponCode, setCouponCode] = useState("");
  useEffect(() => {
    setCouponCode(
      (new URLSearchParams(window.location.search).get("coupon") ?? "").trim().toUpperCase(),
    );
  }, []);
  const couponParam = couponCode ? `&coupon=${encodeURIComponent(couponCode)}` : "";
  const [isEnrolling, setIsEnrolling] = useState(false);

  const { data: catalogData } = useQuery({
    queryKey: queryKeys.catalog.detail(slug),
    queryFn: async () => {
      const t = getToken();
      const res = await fetcher<CatalogSlugResponse>(`/v1/catalog/${slug}`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.success) return null;
      return res.data;
    },
    enabled: isAuthenticated && price.isFree,
    staleTime: 5 * 60 * 1000,
  });

  const catalogItemId = catalogData?.item?._id;

  const ownedCourse = library?.courses.find((c) => c.courseId === courseId);
  const isOwned = !!ownedCourse;
  const isCheckingOwnership = authLoading || (isAuthenticated && libLoading);

  const heightClass = size === "sm" ? "h-11" : "h-[52px]";
  const textClass = size === "sm" ? "text-[15px]" : "text-[16px]";

  if (lockedReason) {
    return (
      <Button
        type="button"
        disabled
        title={lockedReason}
        className={`w-full cursor-not-allowed rounded-[10px] ${heightClass} ${textClass} font-semibold opacity-60 ${className ?? ""}`}
      >
        <span aria-live="polite">{lockedLabel ?? lockedReason}</span>
      </Button>
    );
  }

  if (isCheckingOwnership) {
    // `SITE-CRS01`: the page (and its first paint, before the session check) shows
    // the action it will offer, not "Loading...": it only waits to become clickable.
    return (
      <Button
        disabled
        aria-busy
        className={`w-full rounded-[10px] ${heightClass} ${textClass} font-semibold disabled:opacity-100 ${className ?? ""}`}
      >
        <span aria-live="polite">
          {enrollLabel ??
            (price.isFree ? t("enrollFree") : t("enrollWithPrice", { price: formatPrice(price) }))}
        </span>
      </Button>
    );
  }

  if (isAuthenticated && isOwned) {
    if (enrolledPeerNotice) {
      return (
        <p
          className={`flex w-full items-center justify-center rounded-[10px] border border-neutral-200 bg-neutral-50 px-3 text-center text-[13px] font-medium leading-snug text-neutral-700 ${heightClass} ${textClass} ${className ?? ""}`}
          aria-live="polite"
        >
          {enrolledPeerNotice}
        </p>
      );
    }

    // B4, owned course + upgrade target offered → route to /dashboard/upgrade.
    if (upgradeToPlanId) {
      const upgradeHref = `/${locale}/dashboard/upgrade/${courseId}?target=${upgradeToPlanId}`;
      return (
        <Button
          className={`w-full rounded-[10px] ${heightClass} ${textClass} font-semibold ${className ?? ""}`}
          asChild
        >
          <Link href={upgradeHref}>
            <span aria-live="polite">{upgradeLabel ?? "Upgrade"}</span>
          </Link>
        </Button>
      );
    }

    if (upgradeUnavailableReason) {
      return (
        <Button
          type="button"
          disabled
          title={upgradeUnavailableReason}
          className={`w-full cursor-not-allowed rounded-[10px] ${heightClass} ${textClass} font-semibold opacity-60 ${className ?? ""}`}
        >
          <span aria-live="polite">
            {upgradeUnavailableLabel ?? "Upgrade unavailable"}
          </span>
        </Button>
      );
    }

    const resumeLesson = ownedCourse.progress?.lastLessonId ?? firstLessonId;
    const href = resumeLesson
      ? `/${locale}/courses/${slug}/lessons/${resumeLesson}`
      : `/${locale}/dashboard/library`;

    // Narrow `locale: string` to a known app locale for the copy lookup;
    // fall back to EN if the router handed us something unexpected.
    const narrowLocale: Locale = (locales as readonly string[]).includes(locale)
      ? (locale as Locale)
      : "en";
    const continueLabel = getOwnershipCopy(narrowLocale).continueLearning;

    return (
      <Button
        className={`w-full rounded-[10px] bg-emerald-600 ${heightClass} ${textClass} font-semibold hover:bg-emerald-700 ${className ?? ""}`}
        asChild
      >
        <Link href={href}>{continueLabel}</Link>
      </Button>
    );
  }

  if (isAuthenticated && !isOwned && price.isFree) {
    const handleFreeEnroll = async () => {
      if (!catalogItemId || isEnrolling) return;
      setIsEnrolling(true);
      try {
        const t = getToken();
        await fetcher("/v1/orders", {
          method: "POST",
          headers: t ? { Authorization: `Bearer ${t}` } : {},
          body: JSON.stringify({
            items: [
              {
                catalogItemId,
                ...(pricingPlanId ? { pricingPlanId } : {}),
              },
            ],
          }),
        });
        const lessonHref = firstLessonId
          ? `/${locale}/courses/${slug}/lessons/${firstLessonId}`
          : `/${locale}/dashboard/library`;
        router.push(lessonHref);
      } catch {
        router.push(`/${locale}/checkout?course=${slug}`);
      } finally {
        setIsEnrolling(false);
      }
    };

    return (
      <Button
        onClick={handleFreeEnroll}
        disabled={isEnrolling}
        className={`w-full rounded-[10px] ${heightClass} ${textClass} font-semibold ${className ?? ""}`}
      >
        {isEnrolling && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
        <span aria-live="polite">{enrollLabel ?? t("enrollFree")}</span>
      </Button>
    );
  }

  if (isAuthenticated && !isOwned) {
    const checkoutHref = pricingPlanId
      ? `/${locale}/checkout?course=${slug}&plan=${pricingPlanId}${couponParam}`
      : `/${locale}/checkout?course=${slug}${couponParam}`;
    return (
      <Button
        className={`w-full rounded-[10px] ${heightClass} ${textClass} font-semibold ${className ?? ""}`}
        asChild
      >
        <Link href={checkoutHref}>
          <span aria-live="polite">
            {enrollLabel ?? t("enrollWithPrice", { price: formatPrice(price) })}
          </span>
        </Link>
      </Button>
    );
  }

  const loginRedirect = pricingPlanId
    ? `/${locale}/checkout?course=${slug}&plan=${pricingPlanId}${couponParam}`
    : `/${locale}/courses/${slug}${couponCode ? `?coupon=${encodeURIComponent(couponCode)}` : ""}`;

  return (
    <Button
      className={`w-full rounded-[10px] ${heightClass} ${textClass} font-semibold ${className ?? ""}`}
      asChild
    >
      <Link href={signInHref(locale, loginRedirect)}>
        <span aria-live="polite">
          {enrollLabel ?? (price.isFree ? t("enrollFree") : t("enrollNow"))}
        </span>
      </Link>
    </Button>
  );
}
